/**
 * Which track a manager or an admin rehearses on (§4.1, §13.4).
 *
 * A rep never sees this. Their `salesRole` is assigned, not chosen, and it is
 * what they are certified as. A manager has no sales role — that absence is
 * what marks them as not a rep — so nothing else says which half of the content
 * they should be shown, and every simulation in a pack is authored for one
 * track or the other.
 *
 * Presentational on purpose. The write lives in App, which decides when this
 * screen is on: Firestore applies a write locally before the server sees it, so
 * a rejected one briefly looks like it worked. If this component owned the
 * write, the screen behind it would appear, unmount this one, and the failure
 * that arrived a moment later would have nowhere left to be shown — which is
 * exactly the bug that produced a picker you could not get past and no
 * explanation for why.
 */
export function TrackPicker({ current, busy, error, onChoose, onCancel }: {
  current: string | null;
  /** Which track is mid-save, or null. */
  busy: string | null;
  error: string | null;
  onChoose: (track: 'setter' | 'closer') => void;
  /** Absent on the first choice — there is nothing behind it to go back to. */
  onCancel?: () => void;
}) {
  return (
    <main className="centered">
      <div className="card">
        <h1>Which track do you want to train on?</h1>
        <p className="muted">
          The drills and the standards are the same for both. The practice calls
          and the certification call are not: a setter opens cold and hands over,
          a closer picks up a transfer already in progress.
        </p>

        <div className="track-choice">
          {(['setter', 'closer'] as const).map((track) => (
            <button
              key={track}
              className={current === track ? 'primary wide' : 'ghost wide'}
              disabled={busy !== null}
              onClick={() => onChoose(track)}
            >
              <strong>{busy === track ? 'Saving…' : track === 'setter' ? 'Setter' : 'Closer'}</strong>
              <span className="sub">
                {track === 'setter'
                  ? 'Cold calls, start to transfer'
                  : 'Live transfers picked up mid-call'}
              </span>
            </button>
          ))}
        </div>

        {error && <p className="error" role="alert">{error}</p>}

        <p className="muted small">
          You can switch whenever you like. Each track keeps its own progress, so
          switching back does not lose anything.
        </p>

        {onCancel && <button className="linkish" onClick={onCancel}>Cancel</button>}
      </div>
    </main>
  );
}
